import React, { useState, useEffect } from 'react';
import { TabunganSiswa, Siswa, Kelas } from '../../types/index.js';
import { api } from '../../services/api.js';
import { Search, FileText, Download, Trash2, Printer } from 'lucide-react';
import { useDataTable } from '../../hooks/useDataTable.js';
import { Pagination } from '../ui/Pagination.js';
import { TabunganStatementModal } from './TabunganStatementModal.js';
import { SearchableSelect } from '../ui/SearchableSelect.js';

interface MutasiTabunganViewProps {
  siswaList: Siswa[];
  kelasList: Kelas[];
  currentUser?: any;
}

export const MutasiTabunganView: React.FC<MutasiTabunganViewProps> = ({
  siswaList,
  kelasList,
  currentUser,
}) => {
  const [mutasiList, setMutasiList] = useState<TabunganSiswa[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSiswaId, setSelectedSiswaId] = useState('ALL');
  const [showStatement, setShowStatement] = useState(false);
  
  const isAdmin = currentUser?.role?.toLowerCase() === 'admin' || currentUser?.role?.toLowerCase() === 'superadmin';
  const currentPetugasName = currentUser?.nama || currentUser?.username || 'Teller';
  
  // Default to ALL if admin, else default to current user's name
  const [selectedPetugas, setSelectedPetugas] = useState(isAdmin ? 'ALL' : currentPetugasName);

  useEffect(() => {
    loadMutasi();
  }, []);

  const loadMutasi = async () => {
    setLoading(true);
    try {
      const data = await api.getMutasiTabungan();
      setMutasiList(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelMutasi = async (id: string, keterangan: string) => {
    if (!window.confirm(`Apakah Anda yakin ingin MEMBATALKAN transaksi mutasi ini?\n\nKeterangan: ${keterangan || '-'}\n\nSaldo siswa akan dikembalikan ke sebelum mutasi terjadi.`)) {
      return;
    }
    
    try {
      const res = await api.cancelMutasiTabungan(id);
      if (res.success) {
        alert(res.message);
        loadMutasi();
      } else {
        alert(res.message || 'Gagal membatalkan mutasi');
      }
    } catch (e: any) {
      alert(e.message || 'Terjadi kesalahan sistem');
    }
  };

  const {
    searchQuery: search,
    setSearchQuery: setSearch,
    currentPage,
    setCurrentPage,
    rowsPerPage,
    setRowsPerPage,
    totalPages,
    totalRows,
    paginatedData: filteredMutasi,
  } = useDataTable<TabunganSiswa>({
    data: mutasiList,
    searchFields: ['nis', 'namaSiswa', 'keterangan'],
    initialSortBy: 'tanggal',
    initialSortDir: 'desc',
    customSearchFn: (item, query) => {
      const matchSiswa = selectedSiswaId === 'ALL' || item.siswaId === selectedSiswaId;
      
      // Filter by petugas
      let matchPetugas = true;
      if (selectedPetugas !== 'ALL') {
        matchPetugas = (item.petugas || '').toLowerCase() === selectedPetugas.toLowerCase();
      }
      
      const matchSearch =
        !query ||
        item.nis.includes(query) ||
        (item.namaSiswa || '').toLowerCase().includes(query) ||
        (item.keterangan || '').toLowerCase().includes(query);
      return matchSiswa && matchPetugas && matchSearch;
    }
  });

  // Get unique petugas list for the dropdown filter (only relevant if admin)
  const uniquePetugas = Array.from(new Set(mutasiList.map(m => m.petugas).filter(Boolean)));

  return (
    <div className="space-y-4 animate-in fade-in duration-200">
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-base font-bold text-slate-900">Mutasi Rekening Tabungan</h2>
          <p className="text-xs text-slate-500">
            Daftar histori transaksi Debit & Kredit tabungan siswa
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Cari Transaksi/Nama..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-300 rounded-md focus:ring-1 focus:ring-sky-500 w-52"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-600 hidden md:inline">Pilih Siswa:</span>
            <SearchableSelect
              options={[
                { value: 'ALL', label: '-- Semua Siswa --', searchStr: 'all semua' },
                ...siswaList.map(s => ({ value: s.id, label: `${s.nis} - ${s.nama} (${s.kelasNama})`, searchStr: `${s.nis} ${s.nama} ${s.kelasNama}` }))
              ]}
              value={selectedSiswaId}
              onChange={(val) => setSelectedSiswaId(val)}
              className="min-w-[200px] sm:min-w-[280px]"
            />
            {selectedSiswaId !== 'ALL' && (
              <button
                onClick={() => setShowStatement(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-md shadow-sm transition whitespace-nowrap"
              >
                <Printer className="w-3.5 h-3.5" />
                <span>Cetak E-Statement</span>
              </button>
            )}
          </div>
          
          {isAdmin && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-slate-600 hidden md:inline">Petugas:</span>
              <select
                value={selectedPetugas}
                onChange={(e) => setSelectedPetugas(e.target.value)}
                className="text-xs border border-slate-300 rounded-md px-3 py-1.5 bg-slate-50 focus:ring-1 focus:ring-sky-500 font-medium min-w-[150px]"
              >
                <option value="ALL">-- Semua Petugas --</option>
                {uniquePetugas.map((p, idx) => (
                  <option key={idx} value={p}>{p}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden flex flex-col min-h-[400px]">
        {loading ? (
          <div className="flex-1 flex items-center justify-center text-slate-400">Memuat data mutasi...</div>
        ) : (
          <div className="overflow-x-auto flex-1">
            <table className="w-full text-xs text-left">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-bold uppercase sticky top-0 z-10">
                <tr>
                  <th className="py-2.5 px-3 text-center">Tgl/Waktu</th>
                  <th className="py-2.5 px-3">Siswa</th>
                  <th className="py-2.5 px-3 text-center">Kode</th>
                  <th className="py-2.5 px-3 text-right">Debit (Rp)</th>
                  <th className="py-2.5 px-3 text-right">Kredit (Rp)</th>
                  <th className="py-2.5 px-3 text-right">Saldo Sesudah</th>
                  <th className="py-2.5 px-3">Keterangan</th>
                  <th className="py-2.5 px-3">Petugas</th>
                  <th className="py-2.5 px-3 text-center">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredMutasi.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-slate-400">
                      Tidak ada data mutasi yang cocok
                    </td>
                  </tr>
                ) : (
                  filteredMutasi.map((trx) => {
                    const isKredit = trx.jenis === 'SETOR';
                    const debet = !isKredit ? trx.nominal : 0;
                    const kredit = isKredit ? trx.nominal : 0;
                    
                    return (
                      <tr key={trx.id} className="hover:bg-slate-50 transition">
                        <td className="py-2 px-3 text-center text-slate-500 font-mono whitespace-nowrap">
                          {new Date(trx.tanggal).toLocaleString('id-ID', {
                            year: 'numeric', month: '2-digit', day: '2-digit',
                            hour: '2-digit', minute: '2-digit'
                          })}
                        </td>
                        <td className="py-2 px-3">
                          <span className="font-bold text-slate-800 block">{trx.namaSiswa}</span>
                          <span className="text-[10px] text-slate-400">{trx.nis}</span>
                        </td>
                        <td className="py-2 px-3 text-center font-mono font-bold">
                          {isKredit ? (
                            <span className="text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">ST</span>
                          ) : (
                            <span className="text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">TR</span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-right font-mono text-amber-700">
                          {debet > 0 ? debet.toLocaleString('id-ID') : '-'}
                        </td>
                        <td className="py-2 px-3 text-right font-mono text-emerald-700 font-semibold">
                          {kredit > 0 ? kredit.toLocaleString('id-ID') : '-'}
                        </td>
                        <td className="py-2 px-3 text-right font-mono font-bold text-sky-800">
                          {trx.saldoSesudah.toLocaleString('id-ID')}
                        </td>
                        <td className="py-2 px-3 text-slate-600">{trx.keterangan || '-'}</td>
                        <td className="py-2 px-3 text-slate-500 text-[10px]">{trx.petugas}</td>
                        <td className="py-2 px-3 text-center">
                          <button
                            onClick={() => handleCancelMutasi(trx.id, trx.keterangan || '')}
                            title="Batalkan Transaksi"
                            className="p-1.5 text-rose-500 hover:bg-rose-100 rounded transition"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
        
        {totalPages > 1 && (
          <Pagination 
            currentPage={currentPage}
            totalPages={totalPages}
            totalRows={totalRows}
            rowsPerPage={rowsPerPage}
            onPageChange={setCurrentPage}
            onRowsPerPageChange={setRowsPerPage}
          />
        )}
      </div>

      {showStatement && selectedSiswaId !== 'ALL' && (
        <TabunganStatementModal
          siswa={siswaList.find(s => s.id === selectedSiswaId)!}
          mutasiList={mutasiList.filter(m => m.siswaId === selectedSiswaId)}
          onClose={() => setShowStatement(false)}
        />
      )}
    </div>
  );
};
